import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import {
  $isTextNode,
  DOMConversion,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  DOMExportOutputMap,
  EditorState,
  isHTMLElement,
  Klass,
  LexicalEditor,
  LexicalNode,
  ParagraphNode,
  TextNode,
} from "lexical";
import React from "react";

import { useToken } from "../../hooks/useToken";
import { MyOnChangePlugin } from "./plugins/ChangePlugin";
import { MaxLengthReadOnlyPlugin } from "./plugins/CharacterLimitPlugin";
import ToolbarPlugin from "./plugins/ToolbarPlugin";
import Theme from "./theme";

const placeholder = "Enter some rich text...";

const removeStylesExportDOM = (
  editor: LexicalEditor,
  target: LexicalNode
): DOMExportOutput => {
  const output = target.exportDOM(editor);
  if (output && isHTMLElement(output.element)) {
    for (const el of [
      output.element,
      ...(output.element.querySelectorAll(
        '[style],[class],[dir="ltr"]'
      ) as any),
    ]) {
      el.removeAttribute("class");
      el.removeAttribute("style");
      if (el.getAttribute("dir") === "ltr") {
        el.removeAttribute("dir");
      }
    }
  }
  return output;
};

const exportMap: DOMExportOutputMap = new Map<
  Klass<LexicalNode>,
  (editor: LexicalEditor, target: LexicalNode) => DOMExportOutput
>([
  [ParagraphNode, removeStylesExportDOM],
  [TextNode, removeStylesExportDOM],
]);

const getExtraStyles = (element: HTMLElement): string => {
  let extraStyles = "";
  const fontSize = "";
  const backgroundColor = "";
  const color = "";

  if (fontSize !== "" && fontSize !== "15px") {
    extraStyles += `font-size: ${fontSize};`;
  }
  if (backgroundColor !== "" && backgroundColor !== "rgb(255, 255, 255)") {
    extraStyles += `background-color: ${backgroundColor};`;
  }
  if (color !== "" && color !== "rgb(0, 0, 0)") {
    extraStyles += `color: ${color};`;
  }
  return extraStyles;
};

const constructImportMap = (): DOMConversionMap => {
  const importMap: DOMConversionMap = {};
  const textNodeImportDOM = TextNode.importDOM();

  if (textNodeImportDOM) {
    for (const [tag, fn] of Object.entries(textNodeImportDOM)) {
      importMap[tag] = (
        node: HTMLElement
      ): DOMConversion<HTMLElement> | null => {
        const importer = fn(node);
        if (!importer || !importer.conversion) {
          return null;
        }

        return {
          ...importer,
          conversion: (element: HTMLElement): DOMConversionOutput | null => {
            const output = importer.conversion(element);

            if (!output || !output.forChild || output.node !== null) {
              // Corrigido para garantir que output não seja null aqui se for usado abaixo
              if (output?.node !== null) return output;
              // Se output for null ou não tiver forChild, retorne null ou output
              return output;
            }

            const originalForChild = output.forChild; // Guarda referência caso precise

            return {
              ...output,
              forChild: (
                lexicalNode: LexicalNode,
                parent: LexicalNode | null | undefined
              ): LexicalNode => {
                const extraStyles = getExtraStyles(element);
                if ($isTextNode(lexicalNode) && extraStyles) {
                  lexicalNode.setStyle(lexicalNode.getStyle() + extraStyles);
                }
                return lexicalNode;
              },
            };
          },
        };
      };
    }
  }

  return importMap;
};

export function Editor({
  state,
  onState,
  editable = false,
  maxLength,
  extraStyle = {},
  styles = {},
}: {
  maxLength?: number;
  editable?: boolean;
  state: EditorState | string | null | undefined;
  onState: (data: EditorState) => void;
  extraStyle?: React.CSSProperties;
  styles?: {
    editorContainer?: React.CSSProperties;
    editorInner?: React.CSSProperties;
    editorInput?: React.CSSProperties;
  };
}) {
  const { token } = useToken();
  const placeholderJsx = (
    <div className="editor-placeholder">{placeholder}</div>
  );

  return (
    <LexicalComposer
      initialConfig={{
        html: {
          export: exportMap,
          import: constructImportMap(),
        },
        editable: editable,
        editorState: state,
        namespace: "zeitfy",
        nodes: [ParagraphNode, TextNode],
        onError(error: Error) {
          console.error("Lexical Error:", error);
        },
        theme: Theme,
      }}
    >
      <style>{`
.editor-paragraph {
  margin: 0;
  margin-bottom: 8px;
  color: ${extraStyle?.color || token.colorPrimary};
  position: relative;
}
.editor-placeholder {
  color: #aaa;
  position: absolute;
  top: 15px;
  left: 10px;
  user-select: none;
  pointer-events: none;
  display: inline-block;
}
        `}</style>
      <div
        className="editor-container"
        style={{
          borderRadius: "8px",
          maxWidth: "100%",
          color: "#000",
          position: "relative",
          lineHeight: "20px",
          fontWeight: 400,
          textAlign: "left",
          border: !editable ? "none" : `1px solid ${token.colorBorder}`,
          ...styles?.editorContainer,
        }}
      >
        {editable && <ToolbarPlugin />}
        <div
          className="editor-inner"
          style={{
            backdropFilter: "blur(30px)",
            position: "relative",
            borderRadius: editable ? "0 0 8px 8px" : "8px",
            ...styles?.editorInner,
          }}
        >
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="editor-input"
                style={{
                  minHeight: !editable ? "auto" : "150px",
                  resize: "none",
                  fontSize: "15px",
                  position: "relative",
                  tabSize: 1,
                  outline: "none",
                  padding: !editable ? "0" : "15px 10px",
                  caretColor: "#444",
                  ...styles?.editorInput,
                }}
                aria-placeholder={editable ? placeholder : ""}
                placeholder={(isEditable) =>
                  isEditable ? placeholderJsx : null
                }
              />
            }
            placeholder={(isEditable) => (isEditable ? placeholderJsx : null)}
            ErrorBoundary={LexicalErrorBoundary}
          />
          {editable && <HistoryPlugin />}
          {editable && <AutoFocusPlugin />}
          <MyOnChangePlugin onChange={onState} />
          {!editable && <MaxLengthReadOnlyPlugin maxLength={maxLength} />}
        </div>
      </div>
    </LexicalComposer>
  );
}
